import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { PromoCodeDialogProps } from '../types';

export function PromoCodeDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
  errors = {},
  onClearFieldError,
}: PromoCodeDialogProps) {
  const errorInputCls = 'border-red-500 focus-visible:ring-red-500';
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Код *</Label>
              <Input
                value={formData.code}
                onChange={(e) => { onFormDataChange(prev => ({ ...prev, code: e.target.value.toUpperCase() })); onClearFieldError?.('code'); }}
                placeholder="IMTIHON50"
                aria-invalid={!!errors.code}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200', errors.code && errorInputCls)}
              />
              {errors.code && <p className="text-xs text-red-500">{errors.code}</p>}
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Тип *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: 'percent' | 'fixed') => onFormDataChange(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Процент</SelectItem>
                  <SelectItem value="fixed">Фикс. сумма</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Значение * {formData.type === 'percent' ? '(1-100%)' : '(UZS)'}
              </Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => { onFormDataChange(prev => ({ ...prev, value: parseInt(e.target.value) || 0 })); onClearFieldError?.('value'); }}
                min={1}
                max={formData.type === 'percent' ? 100 : undefined}
                aria-invalid={!!errors.value}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200', errors.value && errorInputCls)}
              />
              {errors.value && <p className="text-xs text-red-500">{errors.value}</p>}
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Макс. использований (пусто = без лимита)</Label>
              <Input
                type="number"
                value={formData.max_uses || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, max_uses: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="Без лимита"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Действует с (необязательно)</Label>
              <Input
                type="datetime-local"
                value={formData.valid_from ? new Date(formData.valid_from).toISOString().slice(0, 16) : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    if (isNaN(date.getTime())) {
                      toast.error('Неверный формат даты');
                      return;
                    }
                    onFormDataChange(prev => ({ ...prev, valid_from: date.toISOString() }));
                  } else {
                    onFormDataChange(prev => ({ ...prev, valid_from: null }));
                  }
                }}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Действует до (необязательно)</Label>
              <Input
                type="datetime-local"
                value={formData.valid_until ? new Date(formData.valid_until).toISOString().slice(0, 16) : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const localDate = new Date(e.target.value + ':00');
                    onFormDataChange(prev => ({ ...prev, valid_until: localDate.toISOString() }));
                  } else {
                    onFormDataChange(prev => ({ ...prev, valid_until: null }));
                  }
                  onClearFieldError?.('valid_until');
                }}
                aria-invalid={!!errors.valid_until}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200', errors.valid_until && errorInputCls)}
              />
              {errors.valid_until && <p className="text-xs text-red-500">{errors.valid_until}</p>}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => onFormDataChange(prev => ({ ...prev, is_active: checked }))}
            />
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Активен</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
